import { pgTable, text, serial, integer, boolean, timestamp, jsonb, uuid, real, varchar, index, unique } from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { nanoid } from "nanoid";

// Session storage table (required for Replit Auth)
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);

// Organization tables
export const organizations = pgTable("organizations", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Users table
export const users = pgTable("users", {
  id: varchar("id").primaryKey().notNull(),
  email: varchar("email"),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  organizationId: varchar("organization_id").notNull(),
  role: text("role").notNull().default("manager"), // admin | manager
  passwordHash: varchar("password_hash"),
  emailVerified: boolean("email_verified").notNull().default(false),
  emailVerificationToken: varchar("email_verification_token"),
  emailVerificationTokenExpiry: timestamp("email_verification_token_expiry"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Password reset tokens
export const passwordResetTokens = pgTable("password_reset_tokens", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  token: varchar("token").notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Org invite tokens (one-time use signup links)
export const orgInviteTokens = pgTable("org_invite_tokens", {
  id: uuid("id").primaryKey().defaultRandom(),
  orgId: varchar("org_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  token: varchar("token").notNull().unique(),
  createdBy: varchar("created_by").references(() => users.id).notNull(),
  targetEmail: varchar("target_email"),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// API tokens table for CLI and automation access
export const apiTokens = pgTable("api_tokens", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(), // Human-readable name for the token
  token: text("token").notNull(), // The actual token value
  userId: varchar("user_id").references(() => users.id).notNull(), // Token owner
  organizationId: varchar("organization_id").notNull(), // Organization context
  isActive: boolean("is_active").notNull().default(true),
  lastUsedAt: timestamp("last_used_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  expiresAt: timestamp("expires_at"), // Optional expiration
});

export const documents = pgTable("documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  originalFilename: text("original_filename").notNull(),
  size: integer("size").notNull(),
  mimeType: text("mime_type").notNull(),
  pageCount: integer("page_count").notNull(),
  currentVersion: integer("current_version").notNull().default(1),
  defaultIndicatorColor: text("default_indicator_color").notNull().default("#ef4444"), // Default red
  organizationId: varchar("organization_id").notNull(), // Match actual database structure
  createdBy: varchar("created_by").notNull(), // Match actual database structure
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const documentVersions = pgTable("document_versions", {
  id: uuid("id").primaryKey().defaultRandom(),
  documentId: uuid("document_id").references(() => documents.id).notNull(),
  version: integer("version").notNull(),
  originalFilename: text("original_filename").notNull(),
  size: integer("size").notNull(),
  pageCount: integer("page_count").notNull(),
  storageUrl: text("storage_url").notNull(),
  processingStatus: text("processing_status").notNull().default("pending"), // pending, processing, completed, failed
  processingProgress: integer("processing_progress").notNull().default(0),
  processingError: text("processing_error"), // Error message if processing fails
  insertedPageIds: text("inserted_page_ids"), // JSON array of page IDs inserted during processing
  hidden: boolean("hidden").notNull().default(false),
  hasOcrData: boolean("has_ocr_data").notNull().default(false), // OCR text extraction completed
  createdBy: text("created_by"), // User ID who uploaded this version (nullable for legacy records)
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(), // Track heartbeat for stuck detection
});

export const documentPages = pgTable("document_pages", {
  id: uuid("id").primaryKey().defaultRandom(),
  versionId: uuid("version_id").references(() => documentVersions.id).notNull(),
  pageNumber: integer("page_number"),  // DEPRECATED: Use displayOrder and array position instead
  imageUrl: text("image_url"),
  webpUrl: text("webp_url"),
  width: integer("width").notNull(),
  height: integer("height").notNull(),
  hidden: boolean("hidden").notNull().default(false),
  displayOrder: integer("display_order").notNull().default(0),
  insertedViaPartialUpload: boolean("inserted_via_partial_upload").notNull().default(false),
  insertedAt: timestamp("inserted_at"),
  isPlaceholder: boolean("is_placeholder").notNull().default(false),
  label: varchar("label", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const pageTextBoxes = pgTable("page_text_boxes", {
  id: uuid("id").primaryKey().defaultRandom(),
  pageId: uuid("page_id").references(() => documentPages.id, { onDelete: "cascade" }).notNull(),
  text: text("text").notNull(),
  x: real("x").notNull(), // Normalized coordinate (0-1)
  y: real("y").notNull(), // Normalized coordinate (0-1)
  width: real("width").notNull(), // Normalized width (0-1)
  height: real("height").notNull(), // Normalized height (0-1)
  confidence: real("confidence").notNull(), // OCR confidence score (0-100)
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const pageIndicators = pgTable("page_indicators", {
  id: uuid("id").primaryKey().defaultRandom(),
  pageId: uuid("page_id").references(() => documentPages.id).notNull(),
  number: integer("number").notNull(), // Sequential numbering per page
  x: integer("x").notNull(), // Absolute pixel coordinate
  y: integer("y").notNull(), // Absolute pixel coordinate
  color: text("color").notNull().default("#ef4444"), // Indicator color
  status: text("status").notNull().default("open"), // open, inProgress, resolved, skipped
  drawingBox: jsonb("drawing_box"), // Optional drawing box {x, y, width, height}
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const documentUsers = pgTable("document_users", {
  id: uuid("id").primaryKey().defaultRandom(),
  documentId: uuid("document_id").references(() => documents.id).notNull(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  active: boolean("active").notNull().default(true), // NEW: Soft delete flag
  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastSeenAt: timestamp("last_seen_at", { withTimezone: true }).defaultNow(), // NEW
});

export const userJoinEvents = pgTable("user_join_events", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id").notNull().references(() => documentUsers.id, { onDelete: "cascade" }),
  documentId: uuid("document_id").notNull().references(() => documents.id, { onDelete: "cascade" }),
  joinedAt: timestamp("joined_at", { withTimezone: true }).defaultNow().notNull(),
});

export const indicatorComments = pgTable("indicator_comments", {
  id: uuid("id").primaryKey().defaultRandom(),
  indicatorId: uuid("indicator_id").references(() => pageIndicators.id, { onDelete: "cascade" }).notNull(),
  userId: uuid("user_id").references(() => documentUsers.id).notNull(),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const commentAttachments = pgTable("comment_attachments", {
  id: uuid("id").primaryKey().defaultRandom(),
  commentId: uuid("comment_id").references(() => indicatorComments.id, { onDelete: "cascade" }).notNull(),
  filename: text("filename").notNull(),
  originalFilename: text("original_filename").notNull(),
  mimeType: text("mime_type").notNull(),
  size: integer("size").notNull(),
  storageUrl: text("storage_url").notNull(),
  thumbnailUrl: text("thumbnail_url").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const commentMentions = pgTable("comment_mentions", {
  id: uuid("id").primaryKey().defaultRandom(),
  commentId: uuid("comment_id").references(() => indicatorComments.id, { onDelete: "cascade" }).notNull(),
  userId: uuid("user_id").references(() => documentUsers.id, { onDelete: "cascade" }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const pageRatings = pgTable("page_ratings", {
  id: uuid("id").primaryKey().defaultRandom(),
  pageId: uuid("page_id").references(() => documentPages.id, { onDelete: "cascade" }).notNull(),
  userId: uuid("user_id").references(() => documentUsers.id, { onDelete: "cascade" }).notNull(),
  rating: integer("rating").notNull(), // 1-5 stars
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const pageViews = pgTable(
  "page_views",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    pageId: uuid("page_id").references(() => documentPages.id, { onDelete: "cascade" }).notNull(),
    documentUserId: uuid("document_user_id").references(() => documentUsers.id, { onDelete: "cascade" }).notNull(),
    versionId: uuid("version_id").references(() => documentVersions.id, { onDelete: "cascade" }).notNull(),
    viewedAt: timestamp("viewed_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    unique("IDX_page_views_page_user").on(table.pageId, table.documentUserId),
  ]
);

export const documentHeatmaps = pgTable("document_heatmaps", {
  id: text("id").primaryKey().$defaultFn(() => nanoid()),
  documentId: uuid("document_id").notNull().references(() => documents.id, { onDelete: "cascade" }),
  versionIdA: uuid("version_id_a").notNull().references(() => documentVersions.id, { onDelete: "cascade" }),
  versionIdB: uuid("version_id_b").notNull().references(() => documentVersions.id, { onDelete: "cascade" }),
  pageNumber: integer("page_number").notNull(),
  heatmapUrl: text("heatmap_url").notNull(), // Google Cloud Storage URL
  diffPixelCount: integer("diff_pixel_count").notNull(),
  totalPixels: integer("total_pixels").notNull(),
  changePercentage: real("change_percentage").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertDocumentSchema = createInsertSchema(documents).pick({
  name: true,
  originalFilename: true,
  size: true,
  mimeType: true,
  pageCount: true,
  defaultIndicatorColor: true,
  organizationId: true,
  createdBy: true,
});

export const updateDocumentSchema = createInsertSchema(documents).pick({
  name: true,
  defaultIndicatorColor: true,
  currentVersion: true,
  pageCount: true,
  size: true,
}).partial();

export const insertDocumentVersionSchema = createInsertSchema(documentVersions).pick({
  documentId: true,
  version: true,
  originalFilename: true,
  size: true,
  pageCount: true,
  storageUrl: true,
  createdBy: true,
});

export const insertDocumentPageSchema = createInsertSchema(documentPages).pick({
  versionId: true,
  width: true,
  height: true,
  displayOrder: true,
}).extend({
  imageUrl: z.string().optional(),
  webpUrl: z.string().optional(),
  hidden: z.boolean().optional(),
  insertedViaPartialUpload: z.boolean().optional(),
  insertedAt: z.coerce.date().optional(),
  isPlaceholder: z.boolean().optional(),
});

export const updateDocumentPageSchema = createInsertSchema(documentPages).pick({
  hidden: true,
  displayOrder: true,
  insertedViaPartialUpload: true,
  insertedAt: true,
  isPlaceholder: true,
}).partial();

export const insertPageIndicatorSchema = createInsertSchema(pageIndicators).pick({
  pageId: true,
  number: true,
  color: true,
}).extend({
  x: z.number().transform(val => Math.round(val)),
  y: z.number().transform(val => Math.round(val)),
  status: z.string().optional(),
  drawingBox: z.any().optional(),
});

export const updatePageIndicatorSchema = createInsertSchema(pageIndicators).pick({
  x: true,
  y: true,
  color: true,
  status: true,
  drawingBox: true,
}).partial();

export const insertDocumentUserSchema = createInsertSchema(documentUsers).omit({
  id: true,
  createdAt: true,
  lastSeenAt: true, // NEW
});

// Schema for request body (excludes documentId since it comes from URL params)
export const createDocumentUserRequestSchema = insertDocumentUserSchema.omit({
  documentId: true,
});

export const insertIndicatorCommentSchema = createInsertSchema(indicatorComments).pick({
  indicatorId: true,
  userId: true,
  content: true,
});

export const insertCommentAttachmentSchema = createInsertSchema(commentAttachments).pick({
  commentId: true,
  filename: true,
  originalFilename: true,
  mimeType: true,
  size: true,
  storageUrl: true,
  thumbnailUrl: true,
});

export const insertCommentMentionSchema = createInsertSchema(commentMentions).pick({
  commentId: true,
  userId: true,
});

export const insertPageRatingSchema = createInsertSchema(pageRatings).pick({
  pageId: true,
  userId: true,
  rating: true,
});

export const updatePageRatingSchema = createInsertSchema(pageRatings).pick({
  rating: true,
});

export const insertPageTextBoxSchema = createInsertSchema(pageTextBoxes).pick({
  pageId: true,
  text: true,
  x: true,
  y: true,
  width: true,
  height: true,
  confidence: true,
});

export const insertPageViewSchema = createInsertSchema(pageViews).pick({
  pageId: true,
  documentUserId: true,
  versionId: true,
});

export type PageView = typeof pageViews.$inferSelect;
export type InsertPageView = z.infer<typeof insertPageViewSchema>;

export const insertDocumentHeatmapSchema = createInsertSchema(documentHeatmaps).pick({
  documentId: true,
  versionIdA: true,
  versionIdB: true,
  pageNumber: true,
  heatmapUrl: true,
  diffPixelCount: true,
  totalPixels: true,
  changePercentage: true,
});

export type Document = typeof documents.$inferSelect;
export type DocumentVersion = typeof documentVersions.$inferSelect;
export type DocumentPage = Omit<typeof documentPages.$inferSelect, 'pageNumber'>;  // Exclude deprecated pageNumber
export type PageIndicator = typeof pageIndicators.$inferSelect;
export type DocumentUser = typeof documentUsers.$inferSelect;
export type UserJoinEvent = typeof userJoinEvents.$inferSelect;
export type IndicatorComment = typeof indicatorComments.$inferSelect;
export type CommentAttachment = typeof commentAttachments.$inferSelect;
export type CommentMention = typeof commentMentions.$inferSelect;
export type PageRating = typeof pageRatings.$inferSelect;
export type DocumentHeatmap = typeof documentHeatmaps.$inferSelect;
export type PageTextBox = typeof pageTextBoxes.$inferSelect;
export type InsertDocument = z.infer<typeof insertDocumentSchema>;
export type UpdateDocument = z.infer<typeof updateDocumentSchema>;
export type InsertDocumentVersion = z.infer<typeof insertDocumentVersionSchema>;
export type InsertDocumentPage = z.infer<typeof insertDocumentPageSchema>;
export type UpdateDocumentPage = z.infer<typeof updateDocumentPageSchema>;
export type InsertPageIndicator = z.infer<typeof insertPageIndicatorSchema>;
export type UpdatePageIndicator = z.infer<typeof updatePageIndicatorSchema>;
export type InsertDocumentUser = z.infer<typeof insertDocumentUserSchema>;
export type InsertUserJoinEvent = typeof userJoinEvents.$inferInsert;
export type InsertIndicatorComment = z.infer<typeof insertIndicatorCommentSchema>;
export type InsertCommentAttachment = z.infer<typeof insertCommentAttachmentSchema>;
export type InsertCommentMention = z.infer<typeof insertCommentMentionSchema>;
export type InsertPageRating = z.infer<typeof insertPageRatingSchema>;
export type UpdatePageRating = z.infer<typeof updatePageRatingSchema>;
export type InsertDocumentHeatmap = z.infer<typeof insertDocumentHeatmapSchema>;
export type InsertPageTextBox = z.infer<typeof insertPageTextBoxSchema>;

export const updateDocumentVersionVisibilitySchema = createInsertSchema(documentVersions).pick({
  hidden: true,
});
export type UpdateDocumentVersionVisibility = z.infer<typeof updateDocumentVersionVisibilitySchema>;

export const documentNotificationSettings = pgTable("document_notification_settings", {
  id: uuid("id").primaryKey().defaultRandom(),
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "cascade" }).notNull(),
  userId: uuid("user_id").references(() => documentUsers.id, { onDelete: "cascade" }).notNull(),
  emailEnabled: boolean("email_enabled").notNull().default(true),
  notificationTypes: text("notification_types").array().notNull().default(["comment_added"]),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const notificationLogs = pgTable("notification_logs", {
  id: uuid("id").primaryKey().defaultRandom(),
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "cascade" }),
  userId: uuid("user_id").references(() => documentUsers.id, { onDelete: "cascade" }),
  recipientEmail: text("recipient_email"), // Fallback when userId is not available
  notificationType: text("notification_type").notNull(),
  status: text("status").notNull().default("sent"), // sent, failed, skipped
  context: jsonb("context"), // { commentId, indicatorId, etc. }
  sentAt: timestamp("sent_at").defaultNow().notNull(),
});

// Insert Schemas
export const insertNotificationSettingsSchema = createInsertSchema(documentNotificationSettings).pick({
  documentId: true,
  userId: true,
  emailEnabled: true,
  notificationTypes: true,
});

export const updateNotificationSettingsSchema = createInsertSchema(documentNotificationSettings).pick({
  emailEnabled: true,
  notificationTypes: true,
}).partial();

export const insertNotificationLogSchema = createInsertSchema(notificationLogs).pick({
  notificationType: true,
  status: true,
  context: true,
}).extend({
  documentId: z.string().nullable().optional(),
  userId: z.string().nullable().optional(),
  recipientEmail: z.string().nullable().optional(),
});

// Relations
export const organizationsRelations = relations(organizations, ({ many }) => ({
  users: many(users),
  documents: many(documents),
}));

export const usersRelations = relations(users, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [users.organizationId],
    references: [organizations.id],
  }),
  createdDocuments: many(documents),
}));

export const documentsRelations = relations(documents, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [documents.organizationId],
    references: [organizations.id],
  }),
  createdBy: one(users, {
    fields: [documents.createdBy],
    references: [users.id],
  }),
  versions: many(documentVersions),
  users: many(documentUsers),
}));

// Organization-level email notification settings
export const orgEmailNotificationSettings = pgTable("org_email_notification_settings", {
  id: uuid("id").primaryKey().defaultRandom(),
  organizationId: varchar("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull().unique(),
  commentAdded: boolean("comment_added").notNull().default(true),
  commentAddedScope: text("comment_added_scope").notNull().default("document_participants"),
  userMentioned: boolean("user_mentioned").notNull().default(true),
  userMentionedScope: text("user_mentioned_scope").notNull().default("document_participants"),
  userJoinedDocument: boolean("user_joined_document").notNull().default(true),
  userJoinedDocumentScope: text("user_joined_document_scope").notNull().default("document_participants"),
  userInvitation: boolean("user_invitation").notNull().default(true),
  userInvitationScope: text("user_invitation_scope").notNull().default("all_users"),
  documentShared: boolean("document_shared").notNull().default(true),
  documentSharedScope: text("document_shared_scope").notNull().default("all_users"),
  versionComparisonReady: boolean("version_comparison_ready").notNull().default(true),
  versionComparisonReadyScope: text("version_comparison_ready_scope").notNull().default("document_participants"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertOrgEmailNotificationSettingsSchema = createInsertSchema(orgEmailNotificationSettings).omit({
  id: true,
  updatedAt: true,
});

export const updateOrgEmailNotificationSettingsSchema = createInsertSchema(orgEmailNotificationSettings).omit({
  id: true,
  organizationId: true,
  updatedAt: true,
}).partial();

export type OrgEmailNotificationSettings = typeof orgEmailNotificationSettings.$inferSelect;
export type InsertOrgEmailNotificationSettings = z.infer<typeof insertOrgEmailNotificationSettingsSchema>;
export type UpdateOrgEmailNotificationSettings = z.infer<typeof updateOrgEmailNotificationSettingsSchema>;

export const NOTIFICATION_TYPES = [
  "comment_added",
  "user_mentioned",
  "user_joined_document",
  "user_invitation",
  "document_shared",
  "version_comparison_ready",
] as const;

export const RECIPIENT_SCOPES = [
  "all_users",
  "admins_only",
  "document_participants",
] as const;

export type NotificationType = typeof NOTIFICATION_TYPES[number];
export type RecipientScope = typeof RECIPIENT_SCOPES[number];

// Insert schemas for organizations and users
export const insertOrganizationSchema = createInsertSchema(organizations);
export const insertUserSchema = createInsertSchema(users);

// Upsert user schema (for legacy migration path)
export const upsertUserSchema = createInsertSchema(users).pick({
  id: true,
  email: true,
  firstName: true,
  lastName: true,
  profileImageUrl: true,
  organizationId: true,
  role: true,
});

// Auth-specific types
export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;
export type OrgInviteToken = typeof orgInviteTokens.$inferSelect;

// Update user profile schema (for account settings)
export const updateUserProfileSchema = createInsertSchema(users).pick({
  firstName: true,
  lastName: true,
  email: true,
}).partial();

// Update organization schema (for org settings)
export const updateOrganizationSchema = createInsertSchema(organizations).pick({
  name: true,
  slug: true,
}).partial();

// Types
export type DocumentNotificationSettings = typeof documentNotificationSettings.$inferSelect;
export type NotificationSettings = DocumentNotificationSettings; // Alias for frontend consistency
export type NotificationLog = typeof notificationLogs.$inferSelect;
export type InsertNotificationSettings = z.infer<typeof insertNotificationSettingsSchema>;
export type UpdateNotificationSettings = z.infer<typeof updateNotificationSettingsSchema>;
export type InsertNotificationLog = z.infer<typeof insertNotificationLogSchema>;

// Organization interfaces
export interface OrganizationStats {
  documentsCount: number;
  usersCount: number;
  commentsCount: number;
  recentActivity: number;
}

export interface ActivityItem {
  id: string;
  type: 'document_created' | 'user_joined' | 'comment_added' | 'version_uploaded';
  message: string;
  userId: string;
  userName: string;
  documentId?: string;
  documentName?: string;
  createdAt: string;
}

// Org-level notification log item (for email activity log in org settings)
export interface OrgNotificationLogItem {
  id: string;
  notificationType: string;
  status: string;
  sentAt: string;
  recipientName: string | null;
  recipientEmail: string | null;
  documentName: string | null;
  documentId: string | null;
}

// Extended document type for dashboard display
export interface DocumentWithCounts extends Document {
  collaboratorCount: number;
  commentCount: number;
  averageRating: number; // 0-5 stars, 0 if no ratings
}

// Invite user schema for organization user invites (only fields sent from frontend)
export const inviteUserSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email is required"),
  role: z.enum(["admin", "manager"]).default("manager"),
});

// API Token schemas
export const insertApiTokenSchema = createInsertSchema(apiTokens).omit({ id: true, token: true, createdAt: true, lastUsedAt: true });
export const apiTokenSchema = createInsertSchema(apiTokens);

// API Token types
export type ApiToken = typeof apiTokens.$inferSelect;
export type InsertApiToken = z.infer<typeof insertApiTokenSchema>;

// Organization types
export type Organization = typeof organizations.$inferSelect;
export type User = typeof users.$inferSelect;
export type InsertOrganization = z.infer<typeof insertOrganizationSchema>;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type UpsertUser = z.infer<typeof upsertUserSchema>;
export type UpdateUserProfile = z.infer<typeof updateUserProfileSchema>;
export type UpdateOrganization = z.infer<typeof updateOrganizationSchema>;
export type InviteUser = z.infer<typeof inviteUserSchema>;
